import baustein from "baustein";
import factory from "web/script/dom/factory";
import globals from "web/script/modules/globals";
import optionTemplate from "templates/modules/universal_filters/option";
import rothko from "web/script/modules/rothko";
import template from "templates/modules/universal_filters/option_group_filtered_scroll_area";
import url from "web/script/utils/url";

const componentName = "universal-filter-option-group-filtered-scroll-area";
const scrollableAreaComponentName = "scrollable-area";

const inputSelector = `.${componentName}__input`;
const spinnerSelector = `.${componentName}__loading-square`;

export default baustein.register(componentName, {
    template: template,

    renderLimit: null,

    setupEvents(add) {
        add("keyup", this.onKeyup);
    },

    init() {
        this.renderLimit = this.options.renderLimit;
        this.client = new rothko.Rothko("modules/option_group_filtered_scroll_area");
        this.client.on("data", this._onGetData.bind(this));
    },

    onInsert() {
        const inputWrapper = this.el.querySelector(
            ".universal-filter-option-group-filtered-scroll-area__input-wrapper"
        );

        if (inputWrapper) {
            inputWrapper.classList.remove("hidden");
        }

        // If the options were passed as parameters, we'll use them. This will happen on the first page load
        if ((this.options.options || []).length > 0) {
            this.hideLoading();
            this.setFilterData(this.options.options, null);
        }

        // this performs the update of the scroll area so its scroll parameters are correct
        this.findComponent(scrollableAreaComponentName).render();
    },

    /**
     * Called when `universal-filters-accordion-section` component is clicked
     * to retrieve options of lazy loaded option group
     */
    fetchOptionGroup() {
        this.updateOptions({
            lazyLoaded: false,
        });
        this.showLoading();

        const parsedUrl = url.parse(globals.window.location.href);
        parsedUrl.searchParams.set("feed_url", parsedUrl.path);
        parsedUrl.searchParams.set("option_group_id", this.options.optionGroupId);
        this.client.fetch(parsedUrl.searchParams.toJSON());
    },

    /**
     * Called when option group are returned by Rothko client
     */
    _onGetData(event, data) {
        const options = data.options;
        this.updateOptions({
            options: options,
        });

        const input = this.el.querySelector(inputSelector);
        const query = input.value.trim().toLowerCase();
        this.setFilterData(options, query);

        this.hideLoading();
        this.findComponent(scrollableAreaComponentName).render();
    },

    showLoading() {
        this.el.querySelector(spinnerSelector).classList.remove("hidden");
        this.findComponent(scrollableAreaComponentName).el.classList.add("hidden");
    },

    hideLoading() {
        this.el.querySelector(spinnerSelector).classList.add("hidden");
        this.findComponent(scrollableAreaComponentName).el.classList.remove("hidden");
    },

    getInitialRenderContext() {
        return {
            filter_option_group: {
                id: "",
                search_placeholder: "",
                options: [],
            },
        };
    },

    /**
     * Overridden to prevent the entire control from re-rendering when the context is set.
     * @param context
     */
    setRenderContext(context) {
        this._renderContext = context;
    },

    onKeyup(event) {
        const input = event.target;

        if (input.tagName !== "INPUT") {
            return;
        }

        const query = input.value.trim().toLowerCase();
        this._filterUsingData(query);

        this.invoke(this.findComponent(scrollableAreaComponentName), "render");
    },

    /**
     * Set the searchable data, and do an initial "filter" with no query.
     * @param data
     * @param query - the query the user typed in
     */
    setFilterData(data, query) {
        this._rawData = data;
        this._filterUsingData(query);
    },

    /**
     * Filter the list with the query term, but using the provided data from the data endpoint
     * that had been provided.
     * @param {String} query - the query the user typed in
     * @private
     */
    _filterUsingData(query) {
        if (!this._rawData) {
            return;
        }

        const limit = this.renderLimit;

        // if we have a query, we want to filter with that query, otherwise, filter by selected
        const filterFunction = query
            ? this._filterWithQuery.bind(this, query)
            : this._filterWithoutQuery;

        // if we have a query OR there are a lot of options, then filter the options,
        // otherwise just render all the options
        let filteredOptions =
            query || this._rawData.length > limit
                ? this._rawData.filter(filterFunction)
                : this._rawData;

        let noMatchSelectedOptions = !query
            ? []
            : this._rawData.filter(this._filterSelectedNotIncludingQuery.bind(this, query));

        if (!query && filteredOptions.length > limit) {
            // too many options, so, nothing
            filteredOptions = [];
        } else if (query && filteredOptions.length > limit) {
            // if we have a query, return the first `limit` options
            filteredOptions = filteredOptions.slice(0, limit);
        }

        this._renderOptions(filteredOptions, noMatchSelectedOptions);
    },

    /**
     * Filter that will find everything that matches the query.
     * @private
     */
    _filterWithQuery(query, option) {
        return option.label.toLowerCase().indexOf(query) >= 0;
    },

    /**
     * Filter that will find everything that is selected.
     * @private
     */
    _filterWithoutQuery(option) {
        return option.checked;
    },

    /**
     * Filter that will find everything that is selected, and then DOES NOT include the query.
     * @private
     */
    _filterSelectedNotIncludingQuery(query, option) {
        return option.checked && option.label.toLowerCase().indexOf(query) === -1;
    },

    /**
     * Renders each option as a checkbox, and then each selected but not matching query
     * as a hidden input
     * @param {Array<*>} options - options that you want to render
     * @param {Array<*>} selectedOptions - selected options that you want to render as hidden
     * @private
     */
    _renderOptions(options, selectedOptions) {
        const currentContent = this.el.querySelector(".scrollable-area-content");

        // render each option
        const renderedOptions = options.map((option) => optionTemplate({ filter_option: option }));

        // render each selected option as a hidden input field
        const selectedRenderedOptions = selectedOptions.map(
            (option) => `<input type="hidden" name="${option.name}" value="${option.value}" />`
        );

        // create a new DOM node to hold these elements
        const newContent = factory.fromString('<div class="scrollable-area-content"></div>');
        newContent.innerHTML = renderedOptions.join("") + selectedRenderedOptions.join("");

        // now do a swap of the old element with the new element
        this.el
            .querySelector(".scrollable-area-inner-wrapper")
            .replaceChild(newContent, currentContent);

        // this performs the update of the scroll area so its scroll parameters are correct
        this.findComponent(scrollableAreaComponentName).render();
    },
});
